<template>
  <div class="hero-section-layout">
    <div class="container">
      <div class="row">
        <div class="col col-6">
          <div class="heading">
            <div class="ratings-holder">
              <span class="rating-grade">{{ course.ratingGrade }}</span>
              <Rating :scale="4.5" :size="16" />
              <span class="reviews-count">({{ course.reviewsCount }})</span>
            </div>
            <h1 class="h3">
              {{ course.name }}
            </h1>
            <p class="instructor">
              With
              <span v-for="(i, k) in course.instructorsArr" :key="k">
                <span>{{ i.name }} </span>
                {{ `${k !== (course.instructorsArr.length - 1) ? ' and ' : ''}` }}
              </span>
            </p>
            <p class="body-1 description">
              {{ course.description }}
            </p>
            <div class="stats">
              <div v-if="course.bundle">
                <IconMonitorPlay class="icon-monitor" />
                {{ course.coursesCount }} Courses
              </div>
              <div>
                <IconMonitorPlay class="icon-monitor" />
                {{ course.lessons }} Lessons
              </div>
              <div>
                <ClockIcon />
                {{ course.totalHours }} hours
              </div>
              <div>
                <CertificateBadge />
                {{ course.bundle ? `${course.coursesCount} Certificates` : 'Certificate' }}
              </div>
            </div>
            <div v-if="course.discountPercent > 0" class="label-accent">
              {{ course.bundle ? 'Special' : 'Limited Time' }} Offer - {{ course.discountPercent }}%
            </div>
            <div class="price">
              <span class="discounted-price h2">${{ course.discountedPrice }}</span>
              <span v-show="parseFloat(course.regularPrice) > parseFloat(course.discountedPrice)" class="regular-price">${{ course.regularPrice }}</span>
            </div>
            <div class="foot">
              <button class="btn-primary" @click="$emit('subscribe')">
                Buy Now
              </button>
              <div class="footnote">
                Billed once. Lifetime access to all course materials and updates.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="wall" :style="{ backgroundImage: `url(${$webp(`${$config.imagesUrl}/courses/${course.bigImage}`)})` }" />
  </div>
</template>

<script>
import ClockIcon from '../../assets/svg/icon-clock.svg?vue-component';
import Rating from '../Rating';
import IconMonitorPlay from '../../assets/svg/icon-monitor-play.svg?vue-components';
import CertificateBadge from '../../assets/svg/certificate-badge.svg?vue-component';

export default {
  components: {
    Rating, IconMonitorPlay, ClockIcon, CertificateBadge,
  },
  props: {
    course: { type: Object, required: true },
  },
};
</script>

<style lang="scss">
.hero-section-layout {
  background-color: var(--main-dark);
  position: relative;
  overflow: hidden;

  .wall {
    position: absolute;
    right: 0;
    top: 0;
    height: 710px;
    left: 0;
    z-index: 0;
    background-position: 66% center;
    background-repeat: no-repeat;
    background-size: cover;
    image-rendering: -webkit-optimize-contrast;
  }

  .container {
    align-items: center;
    min-height: 380px;
    height: 710px;
    position: relative;

    .row {
      align-items: center;
      height: 100%;

      > div {
        height: inherit;
        display: flex;
        align-items: center;
        position: relative;
        color: var(--main-light);
        z-index: 5;
        min-height: 380px;

        .ratings-holder {
          display: flex;
          align-items: center;
          font-size: 14px;

          .rating-grade {
            margin-right: var(--spacing-4);
            color: var(--main-yellow);
            font-weight: 500;
          }

          .reviews-count {
            margin-left: var(--spacing-8);
            color: var(--main-light-shade-4);
          }
        }

        h1 {
          margin: var(--spacing-12) 0;
        }

        .instructor {
          margin: 0 0 var(--spacing-16);
          font-weight: 500;
          font-size: 18px;
        }

        .description {
          margin: 0 0 var(--spacing-12);
          color: var(--main-light-shade-4);
        }

        .stats {
          display: flex;
          margin-bottom: var(--spacing-28);

          > div {
            display: flex;
            align-items: center;
            margin-right: var(--spacing-24);
            color: var(--main-light-shade-4);
            font-size: 18px;
            white-space: nowrap;

            &:last-child {
              margin-right: 0;
            }
          }

          svg {
            width: 22px;
            height: 22px;
            margin-right: var(--spacing-8);
          }

          .icon-monitor {
            path[stroke] {
              stroke: var(--main-light-shade-4);
              stroke-width: 3;
            }

            path[fill] {
              fill: var(--main-light-shade-4);
              stroke-width: 3;
            }
          }
        }

        .label-accent {
          display: inline-block;
          max-width: unset;
          max-height: unset;
          padding: 8px;
          line-height: 100%;
          margin-bottom: 10px;
        }

        .price {
          display: flex;
          align-items: flex-start;

          .discounted-price {
            margin-bottom: 0;
          }

          .regular-price {
            color: var(--main-light-shade-4);
            font-size: 21px;
            font-weight: 500;
            margin-left: var(--spacing-6);
            position: relative;

            &:before {
              content: '';
              top: 50%;
              left: 0;
              right: 0;
              position: absolute;
              transform: translateY(-50%);
              height: 2px;
              width: 100%;
              background-color: var(--main-light-shade-4);
            }
          }
        }

        .foot {
          max-width: 300px;

          .btn-primary {
            margin: var(--spacing-28) 0 var(--spacing-12);
            height: 58px;
          }

          .footnote {
            line-height: 140%;
            letter-spacing: 0.02em;
            color: var(--main-light-shade-4);
          }
        }
      }
    }
  }

  @include bp($bp-laptop) {
    .btn-primary {
      width: 225px;
    }
  }

  @include bp($bp-mobile) {
    display: flex;
    flex-direction: column;

    .wall {
      order: -1;
      position: relative;
      height: 540px;
      background-position: 85%;
      background-size: cover;

      &:after,
      &:before {
        content: '';
        width: 100%;
        height: 100px;
        display: block;
        position: absolute;
        bottom: 0;
        left: 0;
        background: linear-gradient(0deg, rgba(0, 12, 31, 0.98) 6%, rgba(0, 12, 31, 0) 100%);
      }

      &:before {
        background: linear-gradient(180deg, rgba(0, 12, 31, 0.98) 28%, rgba(0, 12, 31, 0) 100%);
        bottom: auto;
        top: 0;
        height: 140px;
      }
    }

    .container {
      height: 100%;

      &:before {
        display: none;
      }

      .row > div {
        align-items: flex-end;
        padding-bottom: 80px;

        .price {
          .discounted-price {
            font-size: 40px;
          }
        }

        .stats {
          display: none;
        }

        .description {
          margin-bottom: var(--spacing-36);
        }
      }
    }
  }

  @include bp($bp-phone) {
    .wall {
      background-position: 74%;
      background-repeat: no-repeat;
      background-size: cover;
      height: 521px;
      width: 100%;
    }

    .container .row > div .foot {
      max-width: 100%;
    }
  }
}
</style>
